/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/**
 * Generic API Client Factory with Token Refresh
 *
 * Axios-based HTTP client factory for all WasteHero microservices.
 * Uses ONLY the IAM Access Token (not the legacy JWT).
 *
 * Features:
 * - Generic factory pattern for multiple microservices
 * - Automatic token refresh on 401 responses
 * - Request queuing during token refresh
 * - Automatic retry of failed requests after refresh
 * - Flexible headers, params, and body passthrough
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import { jwtDecode } from 'jwt-decode'

import { servicesBaseUrl, baseUrl } from 'src/api/baseUrl'
import { TokenStorage } from 'src/utils/tokenStorage'

import { Services } from './services'

// Re-export Services for convenience
export { Services, type ServiceKey, type ServiceName } from './services'

// =============================================================================
// Types
// =============================================================================

/**
 * Options for creating a new API client instance.
 */
export interface CreateClientOptions {
  /** The microservice path (e.g., 'iam', 'notifications', 'fleet', 'billing') */
  service: string
  /** API version (default: 'v1') */
  version?: string
  /** Default headers to include in all requests */
  defaultHeaders?: Record<string, string>
  /** Default query params to include in all requests */
  defaultParams?: Record<string, unknown>
}

/**
 * IAM JWT payload structure.
 * The token is already verified by the IAM service - we just need the claims.
 */
interface IAMTokenPayload {
  sub: string // User ID (format: 'user_{ulid}')
  company_id?: string // Company ULID
  exp: number // Expiration timestamp
  iat: number // Issued at timestamp
  scope?: string // OAuth scopes
  [key: string]: unknown // Allow additional claims
}

/**
 * Token response from IAM service (snake_case as returned by API)
 */
interface TokenResponse {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
  scope?: string
}

// =============================================================================
// Token Management (Shared across all clients)
// =============================================================================

// Token refresh endpoint
const TOKEN_ENDPOINT = '/oauth/token'

/**
 * Safely decode JWT payload without verification.
 */
const decodeJwtPayload = (token: string): IAMTokenPayload | null => {
  try {
    return jwtDecode<IAMTokenPayload>(token)
  } catch {
    return null
  }
}

// Single in-flight refresh promise shared across all callers (axios + Apollo).
// Any concurrent call to refreshAccessToken returns the same promise.
let ongoingRefresh: Promise<string | null> | null = null

/**
 * Attempt to refresh the access token.
 * Deduplicates concurrent calls — all callers await the same promise.
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (ongoingRefresh) return ongoingRefresh

  const refreshToken = TokenStorage.getIAMRefreshToken()
  if (!refreshToken) {
    TokenStorage.clearAll()
    window.location.href = '/login'
    return Promise.resolve(null)
  }

  ongoingRefresh = axios
    .post<TokenResponse>(
      `${servicesBaseUrl}/iam${TOKEN_ENDPOINT}`,
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: 'frontend_web',
        refresh_token: refreshToken,
      }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      }
    )
    .then(({ data }) => {
      TokenStorage.setIAMAccessToken(data.access_token)
      if (data.refresh_token) {
        TokenStorage.setIAMRefreshToken(data.refresh_token)
      }
      return data.access_token
    })
    .catch(() => {
      TokenStorage.clearAll()
      window.location.href = '/login'
      return null
    })
    .finally(() => {
      ongoingRefresh = null
    })

  return ongoingRefresh
}

// =============================================================================
// FastAPI Error Types
// =============================================================================

/**
 * FastAPI/Pydantic validation error format
 */
interface FastAPIValidationError {
  type: string
  loc: (string | number)[]
  msg: string
  input?: unknown
  ctx?: unknown
}

/**
 * FastAPI error response format
 */
interface FastAPIErrorResponse {
  detail: FastAPIValidationError[] | string
}

// =============================================================================
// Shared Interceptors
// =============================================================================

/**
 * Create the request interceptor that adds auth headers.
 * This is shared across all service clients.
 */
const createAuthRequestInterceptor = () => {
  return (config: InternalAxiosRequestConfig) => {
    const token = TokenStorage.getIAMAccessToken()

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
      config.headers['wh-request-id'] = crypto.randomUUID()

      // Decode JWT to extract IAM user_id and company_id for gateway headers
      const payload = decodeJwtPayload(token)
      if (payload) {
        // IAM JWT contains 'sub' (user_id) and 'company_id' claims
        // The 'sub' claim may have a 'user_' prefix that needs to be stripped
        if (payload.sub) {
          const userId = (payload.sub as string).replace(/^user_/, '')
          config.headers['WH-User-Id'] = userId
        }
        if (payload.company_id) {
          config.headers['WH-Company-Id'] = payload.company_id as string
        }
      }
    }

    return config
  }
}

/**
 * Parse FastAPI error response and format user-friendly message.
 * This extracts error details from FastAPI/Pydantic validation errors.
 */
const parseFastAPIError = (error: AxiosError<FastAPIErrorResponse>): string => {
  const responseData = error.response?.data

  if (!responseData?.detail) {
    return error.message
  }

  // Handle array of validation errors
  if (Array.isArray(responseData.detail)) {
    const messages = responseData.detail.map(
      (validationError) => validationError.msg
    )
    return messages.join('. ')
  }

  // Handle string detail
  if (typeof responseData.detail === 'string') {
    return responseData.detail
  }

  return error.message
}

/**
 * Create the response interceptor that enriches FastAPI errors.
 * This adds parsed error messages to error.message for easier error handling.
 */
const createFastAPIErrorInterceptor = () => {
  return (error: AxiosError) => {
    // Only process non-401 errors (401 is handled by refresh interceptor)
    if (error.response?.status !== 401) {
      const parsedMessage = parseFastAPIError(
        error as AxiosError<FastAPIErrorResponse>
      )
      // Enhance error.message with FastAPI error details
      error.message = parsedMessage
    }
    return Promise.reject(error)
  }
}

/**
 * Create the response interceptor that handles 401 and token refresh.
 * This is shared across all service clients.
 */
const createRefreshResponseInterceptor = (client: AxiosInstance) => {
  return async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Only handle 401 errors and avoid infinite loops
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    // Mark this request as retried
    originalRequest._retry = true

    const newToken = await refreshAccessToken()

    if (newToken) {
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
      }
      return client(originalRequest)
    }

    return Promise.reject(error)
  }
}

// =============================================================================
// API Client Factory
// =============================================================================

/**
 * Create an API client for any microservice.
 *
 * The client automatically handles:
 * - OAuth2 token management (Authorization header)
 * - Gateway headers (WH-User-Id, WH-Company-Id from JWT claims)
 * - Token refresh on 401 responses
 * - Request queuing during refresh
 *
 * @example
 * ```typescript
 * // Create a client for any microservice
 * const iamClient = createApiClient({ service: 'iam' })
 * const fleetClient = createApiClient({ service: 'fleet' })
 * const crmClient = createApiClient({ service: 'crm', version: 'v2' })
 *
 * // Create a client with custom defaults
 * const notificationsClient = createApiClient({
 *   service: 'notifications',
 *   defaultHeaders: { 'X-Priority': 'high' }
 * })
 *
 * // Make requests with flexible params/headers/body
 * const response = await iamClient.get('/roles', {
 *   params: { limit: 50 },
 *   headers: { 'X-Custom': 'value' }
 * })
 *
 * // POST with body, headers, and params
 * const result = await fleetClient.post('/vehicles', vehicleData, {
 *   headers: { 'X-Idempotency-Key': uuid },
 *   params: { notify: true }
 * })
 * ```
 */
export const createApiClient = (
  options: CreateClientOptions
): AxiosInstance => {
  const {
    service,
    version = 'v1',
    defaultHeaders = {},
    defaultParams = {},
  } = options

  const client = axios.create({
    baseURL: `${servicesBaseUrl}/${service}/${version}`,
    headers: {
      'Content-Type': 'application/json',
      ...defaultHeaders,
    },
    params: defaultParams,
    paramsSerializer: { indexes: null },
    // Include credentials for cookie-based auth (refresh token)
    withCredentials: true,
  })

  // Add shared interceptors
  client.interceptors.request.use(createAuthRequestInterceptor(), (error) =>
    Promise.reject(error)
  )

  // Note: Response interceptors run in reverse order (last added = first executed)

  // Add FastAPI error parsing interceptor first (runs last, after refresh logic)
  client.interceptors.response.use(
    (response) => response,
    createFastAPIErrorInterceptor()
  )

  // Add token refresh interceptor last (runs first, handles 401 before FastAPI parsing)
  client.interceptors.response.use(
    (response) => response,
    createRefreshResponseInterceptor(client)
  )

  return client
}

// =============================================================================
// Pre-configured Service Clients
// =============================================================================

/** IAM service client - identity and access management */
export const iamClient = createApiClient({ service: Services.IAM })

/** Notifications service client */
export const notificationsClient = createApiClient({
  service: Services.NOTIFICATIONS,
})

/** Billing service client */
export const billingClient = createApiClient({ service: Services.BILLING })

/** Assets service client */
export const assetsClient = createApiClient({
  service: Services.ASSETS,
})

/** Fleet service client */
export const fleetClient = createApiClient({ service: Services.FLEET })

/** System service client */
export const systemClient = createApiClient({ service: Services.SYSTEM })

/** Product & Pricing service client */
export const productPricingClient = createApiClient({
  service: Services.PRODUCTS,
  version: '', // It's intentional to keep the version empty for the product/pricing client, as the generated API paths already include /api/v1, check src/modules/products-and-pricing/api-client/index.ts for details.
})

/** CRM service client */
export const crmClient = createApiClient({ service: Services.CRM })

/** CRM BFF service client */
export const crmBffClient = createApiClient({ service: Services.CRM_BFF })
export const crmViewsClient = createApiClient({
  service: Services.CRM,
  version: 'views/v1',
})

/**
 * Django API client - legacy Django endpoints at /api/v1
 * Uses the GraphQL base URL (e.g., https://staging-pjh.wastehero.io)
 * No auth interceptors - used for public endpoints like password reset
 */
export const djangoClient = axios.create({
  baseURL: `${baseUrl}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Legacy export name for compatibility
export const apiClient = iamClient

// =============================================================================
// IAM Token Claim Utilities
// =============================================================================

/**
 * Get the current IAM JWT claims.
 * Returns null if no token is stored or token is invalid.
 */
export const getIAMClaims = (): Record<string, unknown> | null => {
  const token = TokenStorage.getIAMAccessToken()
  if (!token) return null
  return decodeJwtPayload(token)
}

/**
 * Get the IAM company_id (ULID) from the stored token.
 * This should be used for IAM API calls instead of GraphQL Relay IDs.
 */
export const getIAMCompanyId = (): string | null => {
  const claims = getIAMClaims()
  if (!claims?.company_id) return null
  return claims.company_id as string
}

/**
 * Get the IAM user_id (ULID) from the stored token.
 * The 'sub' claim has format 'user_{ulid}' - we strip the prefix.
 */
export const getIAMUserId = (): string | null => {
  const claims = getIAMClaims()
  if (!claims?.sub) return null
  return (claims.sub as string).replace(/^user_/, '')
}

// Export types for consumers
export type { AxiosRequestConfig, AxiosError }

export default apiClient
